/**
 * Interfaz para las transacciones de consumo
 */
export interface ITransaction {
  id: string;
  fecha: string;
  producto: string;
  monto: number;
  tipo: "consumo" | "carga";
}

/**
 * Interfaz para los productos asignados
 */
export interface IAssignedProduct {
  id: string;
  productoNombre: string;
  cantidadAsignada: number;
  fechaAsignacion: string;
  precioUnitario: number;
  productoId: string;
}

/**
 * Interfaz para las fechas programadas
 */
export interface IScheduledDates {
  assignment: {
    productName: string;
    assignedDate: string;
    assignedQuantity: number;
    productDescription: string;
    productPrice: number;
  };
  summary: {
    totalOrders: number;
    firstDate: string | null;
    lastDate: string | null;
    hasScheduledDates: boolean;
  };
  scheduledDates: Array<{
    date: string;
    orders: Array<{
      orderId: string;
      orderDate: string;
      status: string;
      total: number;
      deliveryDate: string | null;
      items: Array<{
        itemId: string;
        quantity: number;
        price: number;
        productId: string;
        productName: string;
        productDescription: string;
      }>;
    }>;
  }>;
  allOrders: Array<{
    order_id: string;
    order_date: string;
    scheduled_date: string;
    order_status: string;
    order_total: number;
    item_id: string;
    item_quantity: number;
    item_price: number;
    product_id: string;
    product_name: string;
    product_description: string;
  }>;
}

/**
 * Interfaz para un pedido de consumo
 */
export interface IConsumptionOrder {
  orderId: string;
  status: string;
  total: number;
  createdAt: string;
  deliveryDate: string | null;
  lastScanAt: string | null;
  totalScans: number;
  items: IConsumptionOrderItem[];
}

/**
 * Interfaz para un item de pedido de consumo
 */
export interface IConsumptionOrderItem {
  itemId: string;
  productId: string;
  productName: string;
  productDescription: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

/**
 * Interfaz para una compra en el historial
 */
export interface IPurchaseHistory {
  id: string;
  fecha: string;
  producto: string;
  monto: number;
  pedidoId?: string;
  descripcion: string;
}

/**
 * Datos simulados de transacciones por hijo
 */
// const mockTransactionsData: Record<string, ITransaction[]> = {
//   // Benjamín Rojas
//   "uuid-hijo1": [
//     {
//       id: "t1",
//       fecha: "2024-01-15",
//       producto: "Menú del Día",
//       monto: -3500,
//       tipo: "consumo",
//     },
//     {
//       id: "t2",
//       fecha: "2024-01-14",
//       producto: "Bebida Mediana",
//       monto: -1500,
//       tipo: "consumo",
//     },
//     {
//       id: "t3",
//       fecha: "2024-01-13",
//       producto: "Carga de Saldo",
//       monto: 10000,
//       tipo: "carga",
//     },
//     {
//       id: "t4",
//       fecha: "2024-01-12",
//       producto: "Snack",
//       monto: -800,
//       tipo: "consumo",
//     },
//     {
//       id: "t5",
//       fecha: "2024-01-11",
//       producto: "Almuerzo Completo",
//       monto: -4500,
//       tipo: "consumo",
//     },
//     {
//       id: "t6",
//       fecha: "2024-01-10",
//       producto: "Carga de Saldo",
//       monto: 8000,
//       tipo: "carga",
//     },
//   ],

//   // Sofía Rojas
//   "uuid-hijo2": [
//     {
//       id: "t7",
//       fecha: "2024-01-15",
//       producto: "Desayuno",
//       monto: -2000,
//       tipo: "consumo",
//     },
//     {
//       id: "t8",
//       fecha: "2024-01-14",
//       producto: "Carga de Saldo",
//       monto: 5000,
//       tipo: "carga",
//     },
//     {
//       id: "t9",
//       fecha: "2024-01-13",
//       producto: "Bebida Pequeña",
//       monto: -1000,
//       tipo: "consumo",
//     },
//     {
//       id: "t10",
//       fecha: "2024-01-12",
//       producto: "Snack Saludable",
//       monto: -1200,
//       tipo: "consumo",
//     },
//   ],

//   // Matías Rojas
//   "uuid-hijo3": [
//     {
//       id: "t11",
//       fecha: "2024-01-15",
//       producto: "Carga de Saldo",
//       monto: 15000,
//       tipo: "carga",
//     },
//     {
//       id: "t12",
//       fecha: "2024-01-14",
//       producto: "Menú Ejecutivo",
//       monto: -3800,
//       tipo: "consumo",
//     },
//     {
//       id: "t13",
//       fecha: "2024-01-13",
//       producto: "Bebida Grande",
//       monto: -1800,
//       tipo: "consumo",
//     },
//     {
//       id: "t14",
//       fecha: "2024-01-12",
//       producto: "Postre",
//       monto: -900,
//       tipo: "consumo",
//     },
//     {
//       id: "t15",
//       fecha: "2024-01-11",
//       producto: "Almuerzo Vegetariano",
//       monto: -4200,
//       tipo: "consumo",
//     },
//   ],
// };

import { apiClient } from "../core/apiClient";

/**
 * Servicio para obtener el historial de consumos
 *
 * Conecta con el backend para obtener el historial
 * de transacciones de un estudiante específico.
 */
export class ConsumptionService {
  // private static readonly BASE_URL = "/api/v1/parents";

  /**
   * Obtiene el historial de consumos de un estudiante
   */
  static async getHistoryByChildId(childId: string): Promise<IConsumptionOrder[]> {
    try {
      console.log('Llamando endpoint: /api/v1/parents/students/' + childId + '/consumption-history');
      const response = await apiClient.get(`/api/v1/parents/students/${childId}/consumption-history`);
      console.log('✔ Respuesta del endpoint: ►', response.data);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al obtener historial de consumos');
      }
    } catch (error) {
      console.error('Error obteniendo historial de consumos:', error);
      throw error;
    }
  }

  /**
   * Obtiene información básica de un hijo por ID
   *
   * @param childId - ID del hijo
   * @returns Promise con la información del hijo
   */
  static async getChildInfo(
    childId: string
  ): Promise<{ nombres: string; apellidos: string } | null> {
    try {
      // TODO: Implementar endpoint real en el backend
      // Por ahora, usamos el servicio de familia para obtener la información
      const response = await apiClient.get(
        `/api/v1/parents/students/${childId}/info`
      );

      if (response.data.success) {
        const student = response.data.data;
        return {
          nombres: student.nombre,
          apellidos: student.apellido,
        };
      }

      return null;
    } catch (error) {
      console.error("Error fetching child info:", error);
      // Fallback a datos mock si falla la API
      return {
        nombres: "Estudiante",
        apellidos: "Chilenito",
      };
    }
  }

  /**
   * Obtiene los productos asignados a un estudiante
   * @param childId - ID del estudiante
   * @returns Lista de productos asignados
   */
  static async getAssignedProducts(
    childId: string
  ): Promise<IAssignedProduct[]> {
    try {
      console.log(`🔍 Llamando endpoint: /api/v1/parents/students/${childId}/assigned-products`);
      const response = await apiClient.get(
        `/api/v1/parents/students/${childId}/assigned-products`
      );

      console.log('✅ Respuesta del endpoint:', response.data);

      if (response.data.success) {
        return response.data.data;
      }

      console.log('⚠️ Respuesta no exitosa:', response.data);
      return [];
    } catch (error: unknown) {
      console.error("❌ Error fetching assigned products:", error);
      if (error && typeof error === 'object' && 'response' in error) {
        const errorWithResponse = error as { response: { status: number; data: unknown } };
        console.error("Status:", errorWithResponse.response.status);
        console.error("Data:", errorWithResponse.response.data);
      }
      // No usar datos mock, devolver array vacío para ver el error real
      return [];
    }
  }

  /**
   * Obtiene las fechas programadas de pedidos generados por una asignación
   * @param childId - ID del estudiante
   * @param productoCreditoId - ID del crédito producto de la asignación
   * @returns Información de fechas programadas y pedidos generados
   */
  static async getScheduledDates(
    childId: string,
    productoCreditoId: string
  ): Promise<IScheduledDates> {
    try {
      console.log(`🔍 Llamando endpoint: /api/v1/parents/students/${childId}/scheduled-dates/${productoCreditoId}`);
      const response = await apiClient.get(
        `/api/v1/parents/students/${childId}/scheduled-dates/${productoCreditoId}`
      );

      console.log('✅ Respuesta del endpoint de fechas programadas:', response.data);

      if (response.data.success) {
        return response.data.data;
      }

      console.log('⚠️ Respuesta no exitosa de fechas programadas:', response.data);
      throw new Error(response.data.message || 'Error al obtener fechas programadas');
    } catch (error: unknown) {
      console.error("❌ Error fetching scheduled dates:", error);
      if (error && typeof error === 'object' && 'response' in error) {
        const errorWithResponse = error as { response: { status: number; data: unknown } };
        console.error("Status:", errorWithResponse.response.status);
        console.error("Data:", errorWithResponse.response.data);
      }
      throw error;
    }
  }

  /**
   * Obtiene el historial de compras de un estudiante
   * @param childId - ID del estudiante
   * @returns Lista de compras realizadas por el estudiante
   */
  static async getPurchaseHistory(childId: string): Promise<IPurchaseHistory[]> {
    try {
      console.log(`🔍 Llamando endpoint: /api/v1/parents/students/${childId}/purchase-history`);
      const response = await apiClient.get(
        `/api/v1/parents/students/${childId}/purchase-history`
      );

      console.log('✅ Respuesta del endpoint de historial de compras:', response.data);

      if (response.data.success) {
        return response.data.data;
      }

      console.log('⚠️ Respuesta no exitosa de historial de compras:', response.data);
      return [];
    } catch (error: unknown) {
      console.error("❌ Error fetching purchase history:", error);
      if (error && typeof error === 'object' && 'response' in error) {
        const errorWithResponse = error as { response: { status: number; data: unknown } };
        console.error("Status:", errorWithResponse.response.status);
        console.error("Data:", errorWithResponse.response.data);
      }
      return [];
    }
  }
}

export default ConsumptionService;
